import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import BaseCombobox, { SubComboboxProps } from '../../../components/Combobox/BaseCombobox'
import useAxios from '../../../hooks/useAxios'
import { FilePartyRole, MainDto } from '../../../config/filePartyConfig'
import { FilePartyMetaDataDto } from '../../../config/finance/invoiceConfig'
import { useSettings } from '../../../hooks/useSettings'
import { FilePartyRoleDto } from '../../../context/GlobalContext'

export type FilePartyType = MainDto | FilePartyMetaDataDto

type FilePartyGroupType = FilePartyType & {
    label: FilePartyRole | string
    children: FilePartyType[]
}

export type FilePartyComboboxProps = SubComboboxProps<FilePartyType> & {
    fileId?: string
    onLazyLoaded?: (items: FilePartyType[]) => void
}

const groupEntities = (entities: FilePartyType[], filePartyRoles?: FilePartyRoleDto[]): FilePartyGroupType[] => {
    const result: FilePartyGroupType[] = []
    if (entities) {
        const groups: Map<string, FilePartyGroupType> = entities.reduce((map: Map<string, FilePartyGroupType>, party: FilePartyType) => {
            const group: FilePartyGroupType = map.get(party.role) || { ...party, label: party.role, children: [] }
            group.children = [...group.children, party].sort((a, b) => a.displayName.localeCompare(b.displayName))
            map.set(party.role, group)
            return map
        }, new Map<string, FilePartyGroupType>())

        groups.forEach(group => result.push({ ...group, displayName: filePartyRoles.find(role => role.type === group.role)?.label || group.role }))
    }
    return result
}

export default function FilePartyCombobox(props: FilePartyComboboxProps): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()

    const [rootEntities, setRootEntities] = useState<FilePartyGroupType[]>()
    useEffect(() => {
        ;(!props.entities && props.fileId
            ? axios.get<MainDto[]>(`/file/${props.fileId}/party`).then(response => {
                  return response.data
              })
            : Promise.resolve(props.entities || [])
        ).then(result => {
            const grouped = groupEntities(result, settings.filePartyRoles)
            setRootEntities(grouped)
            props.onLazyLoaded?.(result)
        })
    }, [props.entities, props.fileId])

    return (
        <BaseCombobox<FilePartyType, FilePartyGroupType>
            isGroup={entity => entity['children'] !== undefined}
            isSelectableGroup={entity => entity['label'] === undefined}
            getGroupLabel={entity => entity.displayName}
            getGroupValue={entity => entity.pivotId ?? entity['label']}
            getGroupEntities={entity => entity.children}
            getEntityLabel={entity => entity.displayName}
            getEntityValue={entity => entity.pivotId}
            placeholderKey={`base.partycombobox.placeholder`}
            {...props}
            entities={rootEntities}
        />
    )
}
